import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { CustomButton } from "../../../../core/components/CustomButton";

export interface AffiliateStats {
	clicks: number;
	signups: number;
	paidUsers: number;
	grossTrackedAmount: number;
	pendingEarnings: number;
	payableEarnings: number;
	paidEarnings: number;
	blockedEarnings: number;
}

export interface AffiliateCreative {
	id: string;
	title: string;
	dimensions: string;
	format: string;
	previewUrl: string | null;
	downloadUrl: string;
	sourceUrl?: string;
	notes: string;
}

export interface AffiliateVideoAd {
	id: string;
	title: string;
	duration: string;
	videoUrl: string;
	thumbnailUrl: string;
	suggestedCaption: string;
}

export interface PerformancePoint {
	date?: string;
	label?: string;
	clicks: number;
	signups: number;
	paidUsers: number;
	conversionRate: number;
	earnings: number;
}

export interface AffiliateDashboardData {
	status: string;
	code: string;
	promoCode: string;
	link: string;
	assignedCode?: string;
	assignedLink?: string;
	customCodeRequest?: {
		id: string;
		code: string;
		status: "auto_approved" | "needs_review" | "admin_approved" | "rejected";
		active: boolean;
		reasons: string[];
		requestedAt: string | null;
		reviewedAt: string | null;
	} | null;
	disclosureText: string;
	payoutSetupStatus: string;
	payoutSetupMessage: string;
	nextPayoutDate: string | null;
	minimumPayoutProgress: {
		minimumAmount: number;
		payableAmount: number;
		percent: number;
		remainingAmount: number;
	};
	referralJourney: {
		clickedLink: number;
		signedUp: number;
		subscribed: number;
		eligibleCommission: number;
	};
	profile: {
		displayName: string;
		contactEmail?: string;
		payoutPayPalEmail?: string;
	};
	stats: AffiliateStats;
	performance: {
		daily: PerformancePoint[];
		weekly: PerformancePoint[];
		monthly: PerformancePoint[];
	};
	trafficSources: Array<{
		source: string;
		clicks: number;
		signups: number;
		paidUsers: number;
		conversionRate: number;
		earnings: number;
	}>;
	campaignLinks: Array<{
		id: string;
		label: string;
		source: string;
		link: string;
		clicks: number;
		signups: number;
		paidUsers: number;
		earnings: number;
		createdAt: string | null;
	}>;
	marketingKit: {
		banners: AffiliateCreative[];
		captions: string[];
		emailCopy: string[];
		disclosureCopy: string[];
		videoScripts: string[];
		videoAds: AffiliateVideoAd[];
		postingRules: string[];
	};
	payoutHistory: Array<{
		id: string;
		amount: number;
		currency: string;
		status: string;
		provider: string;
		failureReason: string;
		paidAt: string | null;
		createdAt: string | null;
	}>;
	commissionRules: Array<{
		title: string;
		description: string;
	}>;
	notifications: Array<{
		id: string;
		title: string;
		message: string;
		createdAt: string | null;
		tone: "success" | "warning" | "info";
	}>;
	tierProgress: {
		currentTier: string;
		nextTier: string;
		paidUsers: number;
		targetPaidUsers: number;
		percent: number;
	};
	settings?: {
		currency?: string;
	};
}

export type AffiliateDashboardSection =
	| "overview"
	| "journey"
	| "performance"
	| "links"
	| "kit"
	| "payouts"
	| "rules"
	| "notifications"
	| "support";

const dashboardSections: Array<{
	id: AffiliateDashboardSection;
	label: string;
	description: string;
	icon: "home" | "stats" | "links" | "kit" | "payouts" | "more";
}> = [
	{ id: "overview", label: "Dashboard", description: "Earnings and link", icon: "home" },
	{ id: "journey", label: "Referrals", description: "Referral funnel", icon: "stats" },
	{ id: "performance", label: "Reports", description: "Charts and trends", icon: "stats" },
	{ id: "links", label: "Links", description: "Campaign tracking", icon: "links" },
	{ id: "kit", label: "Creatives", description: "Approved assets", icon: "kit" },
	{ id: "payouts", label: "Payouts", description: "Setup and history", icon: "payouts" },
	{ id: "rules", label: "Settings", description: "Commission policy", icon: "more" },
	{ id: "notifications", label: "Updates", description: "Affiliate alerts", icon: "more" },
	{ id: "support", label: "Help", description: "Questions and requests", icon: "more" },
];

const mobileNavSections: Array<{
	id: AffiliateDashboardSection;
	label: string;
	icon: "home" | "stats" | "links" | "kit" | "payouts" | "more";
}> = [
	{ id: "overview", label: "Home", icon: "home" },
	{ id: "performance", label: "Stats", icon: "stats" },
	{ id: "links", label: "Links", icon: "links" },
	{ id: "payouts", label: "Pay", icon: "payouts" },
	{ id: "rules", label: "More", icon: "more" },
];

const moreSectionIds = new Set<AffiliateDashboardSection>([
	"rules",
	"notifications",
	"support",
]);
const sectionIds = new Set(dashboardSections.map((item) => item.id));

const supportSubjectOptions = [
	"Payment or payout issue",
	"Missing commission",
	"Commission dispute",
	"Tracking or attribution issue",
	"Promo code/link problem",
	"Custom link request",
	"Banner/creative asset request",
	"Marketing compliance question",
	"Account/profile update",
	"Tax or payment details",
	"Fraud/self-referral review",
	"Performance report question",
	"Partner onboarding help",
	"Technical problem",
	"Login/access problem",
	"Feature request",
	"Urgent campaign issue",
	"Other",
];

const supportPriorityOptions = [
	"Normal",
	"Time-sensitive",
	"Urgent campaign issue",
];

const contactPreferenceOptions = [
	"Reply by email",
	"Reply in app",
	"No preference",
];

export interface AffiliateSupportDetails {
	priority: string;
	campaign: string;
	contactPreference: string;
	attachmentFile: File | null;
	attachmentName: string;
}

export const normalizeAffiliateSection = (
	value?: string,
): AffiliateDashboardSection =>
	sectionIds.has(value as AffiliateDashboardSection)
		? (value as AffiliateDashboardSection)
		: "overview";

const emptyPoint: PerformancePoint = {
	clicks: 0,
	signups: 0,
	paidUsers: 0,
	conversionRate: 0,
	earnings: 0,
};

const defaultDashboardData: AffiliateDashboardData = {
	status: "",
	code: "",
	promoCode: "",
	link: "",
	assignedCode: "",
	assignedLink: "",
	customCodeRequest: null,
	disclosureText: "",
	payoutSetupStatus: "",
	payoutSetupMessage: "",
	nextPayoutDate: null,
	minimumPayoutProgress: {
		minimumAmount: 100,
		payableAmount: 0,
		percent: 0,
		remainingAmount: 100,
	},
	referralJourney: {
		clickedLink: 0,
		signedUp: 0,
		subscribed: 0,
		eligibleCommission: 0,
	},
	profile: {
		displayName: "",
		contactEmail: "",
		payoutPayPalEmail: "",
	},
	stats: {
		clicks: 0,
		signups: 0,
		paidUsers: 0,
		grossTrackedAmount: 0,
		pendingEarnings: 0,
		payableEarnings: 0,
		paidEarnings: 0,
		blockedEarnings: 0,
	},
	performance: { daily: [], weekly: [], monthly: [] },
	trafficSources: [],
	campaignLinks: [],
	marketingKit: {
		banners: [],
		captions: [],
		emailCopy: [],
		disclosureCopy: [],
		videoScripts: [],
		videoAds: [],
		postingRules: [],
	},
	payoutHistory: [],
	commissionRules: [],
	notifications: [],
	tierProgress: {
		currentTier: "Starter",
		nextTier: "Top tier",
		paidUsers: 0,
		targetPaidUsers: 1,
		percent: 0,
	},
	settings: {
		currency: "AUD",
	},
};

const normalizeDashboardData = (
	value: AffiliateDashboardData,
): AffiliateDashboardData => {
	const source = value as Partial<AffiliateDashboardData>;
	return {
		...defaultDashboardData,
		...source,
		profile: {
			...defaultDashboardData.profile,
			...(source.profile || {}),
		},
		stats: {
			...defaultDashboardData.stats,
			...(source.stats || {}),
		},
		minimumPayoutProgress: {
			...defaultDashboardData.minimumPayoutProgress,
			...(source.minimumPayoutProgress || {}),
		},
		referralJourney: {
			...defaultDashboardData.referralJourney,
			...(source.referralJourney || {}),
		},
		performance: {
			daily: source.performance?.daily || [],
			weekly: source.performance?.weekly || [],
			monthly: source.performance?.monthly || [],
		},
		trafficSources: source.trafficSources || [],
		campaignLinks: source.campaignLinks || [],
		marketingKit: {
			...defaultDashboardData.marketingKit,
			...(source.marketingKit || {}),
			banners: source.marketingKit?.banners || [],
			captions: source.marketingKit?.captions || [],
			emailCopy: source.marketingKit?.emailCopy || [],
			disclosureCopy: source.marketingKit?.disclosureCopy || [],
			videoScripts: source.marketingKit?.videoScripts || [],
			videoAds: source.marketingKit?.videoAds || [],
			postingRules: source.marketingKit?.postingRules || [],
		},
		payoutHistory: source.payoutHistory || [],
		commissionRules: source.commissionRules || [],
		notifications: source.notifications || [],
		tierProgress: {
			...defaultDashboardData.tierProgress,
			...(source.tierProgress || {}),
		},
		settings: {
			...defaultDashboardData.settings,
			...(source.settings || {}),
		},
	};
};

const money = (value: number, currency = "AUD") => {
	const safeCurrency = /^[A-Z]{3}$/.test(currency || "") ? currency : "AUD";
	return Number(value || 0).toLocaleString("en-AU", {
		style: "currency",
		currency: safeCurrency,
	});
};

const numberText = (value: number) =>
	Number(value || 0).toLocaleString("en-AU");

const cleanLabel = (value: string) =>
	String(value || "")
		.replace(/_/g, " ")
		.replace(/\b\w/g, (letter) => letter.toUpperCase());

const dateText = (value: string | null) =>
	value
		? new Date(value).toLocaleDateString("en-AU", {
				day: "2-digit",
				month: "short",
				year: "numeric",
			})
		: "Not scheduled yet";

const affiliateCampaignSlug = (value: string) =>
	String(value || "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 80);

const buildVideoAffiliateLink = (
	data: AffiliateDashboardData,
	video: AffiliateVideoAd,
) => {
	const baseLink =
		data.assignedLink && data.assignedLink.includes("/auth/login")
			? data.assignedLink
			: data.link && data.link.includes("/auth/login")
				? data.link
				: data.assignedLink || data.link;
	if (!baseLink) return "";
	try {
		const url = new URL(baseLink);
		url.searchParams.set("aff_source", "youtube");
		url.searchParams.set("aff_campaign", affiliateCampaignSlug(video.title));
		return url.toString();
	} catch {
		return baseLink;
	}
};

const buildVideoSharePost = (
	data: AffiliateDashboardData,
	video: AffiliateVideoAd,
) =>
	[
		video.suggestedCaption,
		"",
		`Watch the ad: ${video.videoUrl}`,
		`Join through my link: ${buildVideoAffiliateLink(data, video)}`,
	].join("\n");

const customCodeStatusText = (
	request?: AffiliateDashboardData["customCodeRequest"],
) => {
	if (!request) return "No personal code requested yet.";
	if (request.status === "rejected") return "This request was declined.";
	if (request.status === "needs_review") return "Waiting for review.";
	if (request.active) return "Active.";
	return "Approved.";
};

const conversionRate = (stats: AffiliateStats) =>
	stats.clicks > 0 ? Math.round((stats.signups / stats.clicks) * 100) : 0;

const latestPoint = (rows: PerformancePoint[]) =>
	rows.length > 0 ? rows[rows.length - 1] : emptyPoint;

const panelClass =
	"overflow-hidden rounded-lg border border-slate-200/80 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.07)]";
const inputClass =
	"min-h-[52px] w-full rounded-md border border-slate-200 bg-white px-4 text-[16px] text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 sm:text-sm";
const textareaClass = `${inputClass} min-h-[112px] py-3`;

const AppIcon = ({
	name,
	className = "h-6 w-6",
}: {
	name: "home" | "stats" | "links" | "kit" | "payouts" | "more";
	className?: string;
}) => {
	const common = {
		className,
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: "currentColor",
		strokeWidth: 2,
		strokeLinecap: "round" as const,
		strokeLinejoin: "round" as const,
		"aria-hidden": true,
	};
	if (name === "stats") {
		return (
			<svg {...common}>
				<path d="M4 19V5" />
				<path d="M4 19h16" />
				<path d="M8 16v-5" />
				<path d="M12 16V8" />
				<path d="M16 16v-3" />
			</svg>
		);
	}
	if (name === "links") {
		return (
			<svg {...common}>
				<path d="M10 13a5 5 0 0 0 7.07 0l1.4-1.4a5 5 0 0 0-7.07-7.07L10.6 5.3" />
				<path d="M14 11a5 5 0 0 0-7.07 0l-1.4 1.4a5 5 0 0 0 7.07 7.07l.8-.8" />
			</svg>
		);
	}
	if (name === "kit") {
		return (
			<svg {...common}>
				<path d="M6 7h12" />
				<path d="M7 7v12h10V7" />
				<path d="M9 7a3 3 0 0 1 6 0" />
				<path d="M9.5 12h5" />
				<path d="M9.5 15h3" />
			</svg>
		);
	}
	if (name === "payouts") {
		return (
			<svg {...common}>
				<rect x="3" y="6" width="18" height="12" rx="3" />
				<path d="M3 10h18" />
				<path d="M7 15h4" />
				<path d="M16 15h1" />
			</svg>
		);
	}
	if (name === "more") {
		return (
			<svg {...common}>
				<path d="M5 12h.01" />
				<path d="M12 12h.01" />
				<path d="M19 12h.01" />
			</svg>
		);
	}
	return (
		<svg {...common}>
			<path d="M4 11.5 12 5l8 6.5" />
			<path d="M6.5 10.5V19h11v-8.5" />
			<path d="M10 19v-5h4v5" />
		</svg>
	);
};

const StatusMessage = ({
	tone,
	children,
}: {
	tone: "error" | "success";
	children: ReactNode;
}) => {
	const className =
		tone === "error"
			? "border-red-200 bg-red-50 text-red-800"
			: "border-emerald-200 bg-emerald-50 text-emerald-800";
	return (
		<div className={`rounded-md border px-4 py-3 text-sm shadow-sm ${className}`}>
			{children}
		</div>
	);
};

const MetricTile = ({
	label,
	value,
	tone = "default",
	detail,
}: {
	label: string;
	value: string;
	tone?: "default" | "green" | "amber" | "pink" | "red";
	detail?: string;
}) => {
	const toneClass =
		tone === "green"
			? "text-emerald-700"
			: tone === "amber"
				? "text-amber-700"
				: tone === "pink"
					? "text-pink-700"
						: tone === "red"
							? "text-red-700"
							: "text-slate-950";
	return (
		<div className="min-h-[118px] rounded-lg border border-slate-200/90 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.05)] sm:min-h-[108px]">
			<div className="text-sm font-semibold leading-5 text-slate-500">
				{label}
			</div>
			<div className={`mt-2 break-words text-[26px] font-semibold leading-tight sm:text-2xl ${toneClass}`}>
				{value}
			</div>
			{detail && (
				<div className="mt-2 text-xs font-semibold leading-5 text-pink-600">
					{detail}
				</div>
			)}
		</div>
	);
};

const PageHeader = ({
	title,
	description,
	action,
}: {
	title: string;
	description: string;
	action?: ReactNode;
}) => (
	<div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-4 lg:flex-row lg:items-start lg:justify-between">
		<div>
			<h2 className="text-xl font-semibold text-slate-950 sm:text-2xl">{title}</h2>
			<p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
				{description}
			</p>
		</div>
		{action && <div className="shrink-0">{action}</div>}
	</div>
);

const ProgressBar = ({ percent }: { percent: number }) => (
	<div className="h-2 overflow-hidden rounded-full bg-slate-100">
		<div
			className="h-full rounded-full bg-pink-600 shadow-[0_0_18px_rgba(219,39,119,0.35)]"
			style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
		/>
	</div>
);

const GrowthLineChart = ({
	rows,
	currency,
}: {
	rows: PerformancePoint[];
	currency: string;
}) => {
	const safeRows = rows.length > 0 ? rows.slice(-10) : [emptyPoint];
	const max = Math.max(...safeRows.map((row) => row.earnings || row.clicks), 1);
	const points = safeRows.map((row, index) => {
		const value = row.earnings || row.clicks;
		const x = safeRows.length === 1 ? 0 : (index / (safeRows.length - 1)) * 100;
		const y = 84 - (value / max) * 70;
		return { x, y, row };
	});
	const line = points.map((point) => `${point.x},${point.y}`).join(" ");
	const area = `0,92 ${line} 100,92`;
	const hasActivity = safeRows.some((row) => row.earnings > 0 || row.clicks > 0);

	return (
		<div className="rounded-lg border border-slate-200/80 bg-white p-5 shadow-[0_12px_30px_rgba(15,23,42,0.04)]">
			<div className="flex items-start justify-between gap-4">
				<div>
					<div className="text-sm font-semibold text-slate-950">Growth</div>
					<div className="mt-1 text-xs font-medium text-slate-500">
						Newest activity on the right
					</div>
				</div>
				<div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
					Last 30 days
				</div>
			</div>
			{hasActivity ? (
				<div className="mt-5">
					<svg
						viewBox="0 0 100 96"
						className="h-56 w-full overflow-visible"
						preserveAspectRatio="none"
						aria-label="Growth chart"
					>
						<defs>
							<linearGradient id="affiliateGrowthFill" x1="0" x2="0" y1="0" y2="1">
								<stop offset="0%" stopColor="#ec4899" stopOpacity="0.28" />
								<stop offset="100%" stopColor="#ec4899" stopOpacity="0" />
							</linearGradient>
						</defs>
						{[18, 42, 66, 90].map((y) => (
							<line
								key={y}
								x1="0"
								x2="100"
								y1={y}
								y2={y}
								stroke="#e5e7eb"
								strokeWidth="0.5"
							/>
						))}
						<polygon points={area} fill="url(#affiliateGrowthFill)" />
						<polyline
							points={line}
							fill="none"
							stroke="#ec008c"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2.4"
						/>
					</svg>
					<div className="mt-2 grid grid-cols-3 text-xs font-medium text-slate-400">
						<span>{safeRows[0]?.label || safeRows[0]?.date || "Start"}</span>
						<span className="text-center">
							{safeRows[Math.floor(safeRows.length / 2)]?.label ||
								safeRows[Math.floor(safeRows.length / 2)]?.date ||
								"Middle"}
						</span>
						<span className="text-right">
							{safeRows[safeRows.length - 1]?.label ||
								safeRows[safeRows.length - 1]?.date ||
								"Now"}
						</span>
					</div>
				</div>
			) : (
				<div className="mt-5 flex h-56 items-center justify-center rounded-md bg-slate-50 text-sm font-semibold text-slate-500">
					No growth activity yet
				</div>
			)}
			<div className="mt-4 text-sm font-semibold text-slate-950">
				{money(safeRows.reduce((total, row) => total + row.earnings, 0), currency)} tracked
			</div>
		</div>
	);
};

const SignupActivity = ({ data }: { data: AffiliateDashboardData }) => {
	const rows = [
		["Link clicks", data.referralJourney.clickedLink],
		["Signups", data.referralJourney.signedUp],
		["Paid members", data.referralJourney.subscribed],
		["Eligible", data.referralJourney.eligibleCommission],
	];
	return (
		<div className="rounded-lg border border-slate-200/80 bg-white p-5 shadow-[0_12px_30px_rgba(15,23,42,0.04)]">
			<div className="text-sm font-semibold text-slate-950">Signup activity</div>
			<div className="mt-4 grid gap-3">
				{rows.map(([label, value]) => (
					<div key={label} className="flex items-center justify-between gap-4">
						<span className="text-sm font-medium text-slate-500">{label}</span>
						<span className="text-sm font-semibold text-slate-950">
							{numberText(Number(value || 0))}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

const RecentReferralActivity = ({
	data,
	currency,
}: {
	data: AffiliateDashboardData;
	currency: string;
}) => {
	const rows = data.campaignLinks.slice(0, 4);
	return (
		<div className="rounded-lg border border-slate-200/80 bg-white p-5 shadow-[0_12px_30px_rgba(15,23,42,0.04)]">
			<div className="text-sm font-semibold text-slate-950">Recent referrals</div>
			<div className="mt-4 grid gap-3">
				{rows.length === 0 ? (
					<div className="rounded-md bg-slate-50 p-4 text-sm font-medium text-slate-500">
						No campaign referrals yet.
					</div>
				) : (
					rows.map((row) => (
						<div
							key={row.id}
							className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-b-0 last:pb-0"
						>
							<div className="min-w-0">
								<div className="truncate text-sm font-semibold text-slate-950">
									{row.label}
								</div>
								<div className="mt-1 text-xs font-medium text-slate-500">
									{numberText(row.signups)} signups · {cleanLabel(row.source)}
								</div>
							</div>
							<div className="rounded bg-pink-50 px-2 py-1 text-xs font-semibold text-pink-700">
								{money(row.earnings, currency)}
							</div>
						</div>
					))
				)}
			</div>
		</div>
	);
};

const CopyButton = ({
	text,
	label = "Copy",
	wide = false,
}: {
	text: string;
	label?: string;
	wide?: boolean;
}) => {
	const [copied, setCopied] = useState(false);
	const copy = async () => {
		if (!text) return;
		await navigator.clipboard.writeText(text);
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1600);
	};
	return (
		<CustomButton small full={wide} onClick={copy}>
			{copied ? "Copied" : label}
		</CustomButton>
	);
};

const SparkBars = ({ rows }: { rows: PerformancePoint[] }) => {
	const safeRows = rows.length > 0 ? rows.slice(-18) : [emptyPoint];
	const max = Math.max(...safeRows.map((row) => row.clicks), 1);
	const hasActivity = safeRows.some((row) => row.clicks > 0);
	if (!hasActivity) {
		return (
			<div className="flex h-48 items-center justify-center rounded-md border border-slate-200 bg-slate-50 p-4 text-sm font-medium text-slate-500">
				No daily click activity yet
			</div>
		);
	}
	return (
		<div className="grid h-48 grid-cols-[repeat(18,minmax(8px,1fr))] items-end gap-2 rounded-md border border-slate-200 bg-slate-50 p-4">
			{safeRows.map((row, index) => {
				const height =
					row.clicks > 0
						? Math.max(12, Math.round((row.clicks / max) * 148))
						: 0;
				return (
					<div
						key={`${row.date || row.label || "row"}-${index}`}
						className="w-full rounded-t bg-pink-600"
						style={{ height: `${height}px`, backgroundColor: "#db2777" }}
						title={`${row.date || row.label || "Day"}: ${row.clicks} clicks`}
					/>
				);
			})}
		</div>
	);
};

const MetricBars = ({
	rows,
	valueFor,
	formatter,
	periodLabel,
}: {
	rows: PerformancePoint[];
	valueFor: (row: PerformancePoint) => number;
	formatter: (value: number) => string;
	periodLabel?: string;
}) => {
	const safeRows = rows.length > 0 ? rows.slice(-12) : [emptyPoint];
	const max = Math.max(...safeRows.map(valueFor), 1);
	const hasActivity = safeRows.some((row) => valueFor(row) > 0);
	if (!hasActivity) {
		return (
			<div>
				<div className="flex h-28 items-center justify-center rounded-md bg-slate-50 p-3 text-xs font-medium text-slate-500">
					No activity yet
				</div>
				{periodLabel && (
					<div className="mt-2 text-xs font-medium text-slate-500">
						{periodLabel}
					</div>
				)}
			</div>
		);
	}
	return (
		<div>
			<div className="grid h-28 grid-cols-[repeat(12,minmax(10px,1fr))] items-end gap-2 rounded-md bg-slate-50 p-3">
				{safeRows.map((row, index) => {
					const value = valueFor(row);
					const height =
						value > 0 ? Math.max(10, Math.round((value / max) * 84)) : 0;
					return (
						<div
							key={`${row.date || row.label || "metric"}-${index}`}
							className="w-full rounded-t bg-slate-900"
							style={{ height: `${height}px`, backgroundColor: "#111827" }}
							title={`${row.date || row.label || "Period"}: ${formatter(value)}`}
						/>
					);
				})}
			</div>
			{periodLabel && (
				<div className="mt-2 text-xs font-medium text-slate-500">
					{periodLabel}
				</div>
			)}
		</div>
	);
};

const DataTable = ({
	columns,
	rows,
	emptyText,
}: {
	columns: string[];
	rows: ReactNode[][];
	emptyText: string;
}) => (
	<div>
		<div className="grid gap-3 md:hidden">
			{rows.length === 0 ? (
				<div className="rounded-lg border border-slate-200 bg-white p-4 text-[15px] leading-6 text-slate-500 shadow-sm">
					{emptyText}
				</div>
			) : (
				rows.map((row, rowIndex) => (
					<div
						key={rowIndex}
						className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm shadow-slate-100/80"
					>
						<div className="grid gap-3">
							{row.map((cell, cellIndex) => (
								<div
									key={cellIndex}
									className="grid gap-1 border-b border-slate-100 pb-3 last:border-b-0 last:pb-0"
								>
									<div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
										{columns[cellIndex]}
									</div>
									<div className="min-w-0 break-words text-[15px] leading-6 text-slate-800">
										{cell}
									</div>
								</div>
							))}
						</div>
					</div>
				))
			)}
		</div>
		<div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm md:block">
			<table className="w-full min-w-[640px] text-left text-sm">
			<thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
				<tr>
					{columns.map((column) => (
						<th key={column} className="px-4 py-3">
							{column}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.length === 0 ? (
					<tr>
						<td className="px-4 py-5 text-slate-500" colSpan={columns.length}>
							{emptyText}
						</td>
					</tr>
				) : (
					rows.map((row, rowIndex) => (
						<tr key={rowIndex} className="border-t border-slate-100">
							{row.map((cell, cellIndex) => (
								<td key={cellIndex} className="px-4 py-4 text-slate-700">
									{cell}
								</td>
							))}
						</tr>
					))
				)}
			</tbody>
			</table>
		</div>
	</div>
);

const ShortcutLink = ({
	to,
	icon,
	title,
	description,
}: {
	to: string;
	icon: "home" | "stats" | "links" | "kit" | "payouts" | "more";
	title: string;
	description: string;
}) => (
	<Link
		to={to}
		className="group flex min-h-[98px] items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.05)] transition hover:-translate-y-0.5 hover:border-pink-200 active:scale-[0.99] active:bg-slate-50 sm:min-h-[86px]"
	>
		<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-pink-50 text-pink-700 group-hover:bg-pink-100 group-active:bg-pink-100">
			<AppIcon name={icon} className="h-6 w-6" />
		</div>
		<div className="min-w-0">
			<div className="text-[15px] font-semibold leading-5 text-slate-950">
				{title}
			</div>
			<div className="mt-1 text-[13px] leading-5 text-slate-500">
				{description}
			</div>
		</div>
	</Link>
);

const WorkspaceShell = ({
	title,
	subtitle,
	contextLabel,
	basePath,
	activeSection,
	headerStats,
	children,
}: {
	title: string;
	subtitle: string;
	contextLabel: string;
	basePath: string;
	activeSection: AffiliateDashboardSection;
	headerStats: Array<{ label: string; value: string }>;
	children: ReactNode;
}) => {
	const location = useLocation();
	const search = location.search || "";
	const buildPath = (section: AffiliateDashboardSection) =>
		`${section === "overview" ? basePath : `${basePath}/${section}`}${search}`;
	const isMoreActive = moreSectionIds.has(activeSection);

	return (
		<div className="affiliate-dashboard-scroll-root min-h-full overflow-x-hidden bg-[#0d0d12] text-slate-950">
			<div className="flex min-h-full w-full flex-col lg:flex-row">
				<aside className="hidden min-w-0 overflow-hidden bg-[#0b0b10] text-white lg:block lg:w-[276px]">
					<div className="px-6 pb-7 pt-6">
						<div className="flex items-center gap-3">
							<img
								src="/unjabbed-logo.png"
								alt="Unjabbed"
								className="h-8 w-8 rounded-md object-contain"
							/>
							<div>
								<div className="text-lg font-semibold leading-6 text-white">
									Unjabbed
								</div>
								<div className="text-xs font-medium text-slate-400">
									Affiliate hub
								</div>
							</div>
						</div>
						<div className="mt-7 rounded-lg border border-white/10 bg-white/[0.03] p-4">
							<div className="text-xs font-semibold uppercase text-slate-500">
								Workspace
							</div>
							<div className="mt-2 text-sm font-semibold text-white">
								{contextLabel}
							</div>
						</div>
					</div>
					<nav className="grid w-full gap-1 px-4 pb-5">
						{dashboardSections.map((item) => {
							const active = item.id === activeSection;
							return (
								<Link
									key={item.id}
									to={buildPath(item.id)}
									className={`flex min-w-0 items-center gap-3 rounded-md px-3 py-3 transition ${
										active
											? "bg-pink-600/25 text-pink-200 shadow-[inset_3px_0_0_#ec008c]"
											: "text-slate-400 hover:bg-white/[0.06] hover:text-white"
									}`}
								>
									<AppIcon name={item.icon} className="h-4 w-4 shrink-0" />
									<div className="min-w-0">
										<div className="truncate text-sm font-semibold">{item.label}</div>
										<div className={`mt-0.5 truncate text-xs ${active ? "text-pink-100/70" : "text-slate-500"}`}>
											{item.description}
										</div>
									</div>
								</Link>
							);
						})}
					</nav>
					<div className="mt-auto px-6 pb-6">
						<div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 text-xs leading-5 text-slate-400">
							<div className="font-semibold text-slate-200">Need help?</div>
							<div className="mt-1">Send a support request from the Help tab.</div>
						</div>
					</div>
				</aside>

				<main className="min-w-0 flex-1 overflow-x-hidden bg-[#f6f7fb]">
					<header className="sticky top-0 z-20 border-b border-slate-200/70 bg-white/95 px-4 py-4 backdrop-blur sm:px-5 lg:static lg:px-8 lg:py-7">
						<div className="flex flex-col gap-6 2xl:flex-row 2xl:items-end 2xl:justify-between">
							<div className="min-w-0">
								<div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-pink-700 lg:hidden">
									<img
										src="/unjabbed-logo.png"
										alt=""
										className="h-5 w-5 rounded object-contain"
									/>
									Unjabbed Affiliates
								</div>
								<h1 className="break-words text-2xl font-semibold leading-tight text-slate-950 sm:text-3xl">
									{title}
								</h1>
								<p className="mt-1 max-w-3xl break-words text-sm leading-6 text-slate-600 sm:mt-2">
									{subtitle}
									</p>
								</div>
							<div className="grid grid-cols-2 gap-3 2xl:w-[500px]">
								{headerStats.map((item) => (
									<div
										key={item.label}
										className="min-h-[86px] rounded-lg border border-slate-200 bg-slate-50/80 p-4 shadow-sm sm:min-h-[74px] sm:p-3"
									>
										<div className="text-[13px] font-medium leading-5 text-slate-500 sm:text-xs">
											{item.label}
										</div>
										<div className="mt-1 break-words text-xl font-semibold leading-tight text-slate-950 sm:text-lg">
											{item.value}
										</div>
									</div>
								))}
							</div>
						</div>
					</header>
					<div className="px-4 py-5 pb-32 sm:px-5 lg:px-8 lg:py-7 lg:pb-8">
						<div className="mx-auto w-full max-w-[1480px]">{children}</div>
					</div>
				</main>
			</div>
			<nav className="fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-[#0b0b10]/95 px-2 pb-[max(0.9rem,env(safe-area-inset-bottom))] pt-2.5 shadow-[0_-10px_30px_rgba(15,23,42,0.18)] backdrop-blur lg:hidden">
				<div className="mx-auto grid max-w-[520px] grid-cols-5 gap-1">
					{mobileNavSections.map((item) => {
						const active =
							item.id === activeSection ||
							(item.id === "rules" && isMoreActive);
						return (
							<Link
								key={item.id}
								to={buildPath(item.id)}
								className={`flex min-h-[72px] flex-col items-center justify-center gap-1.5 rounded-md px-1 text-[13px] font-semibold leading-4 transition ${
									active
										? "bg-pink-600/25 text-pink-200"
										: "text-slate-400 active:bg-white/10"
								}`}
							>
								<AppIcon name={item.icon} className="h-7 w-7" />
								<span>{item.label}</span>
							</Link>
						);
					})}
				</div>
			</nav>
		</div>
	);
};

export const AffiliateDashboardWorkspace = ({
	data: rawData,
	section,
	basePath,
	contextLabel,
	loading,
	error,
	message,
	payoutPayPalEmail,
	campaignLabel,
	campaignSource,
	customCode,
	supportSubject,
	supportMessage,
	saving,
	onPayoutPayPalEmailChange,
	onCampaignLabelChange,
	onCampaignSourceChange,
	onCustomCodeChange,
	onSupportSubjectChange,
	onSupportMessageChange,
	onSavePayoutEmail,
	onStartPayoutSetup,
	onCreateCampaignLink,
	onRequestCustomCode,
	onSendSupportRequest,
	supportAttachmentsEnabled = false,
}: {
	data: AffiliateDashboardData | null;
	section: AffiliateDashboardSection;
	basePath: string;
	contextLabel: string;
	loading: boolean;
	error: string;
	message?: string;
	payoutPayPalEmail: string;
	campaignLabel: string;
	campaignSource: string;
	customCode: string;
	supportSubject: string;
	supportMessage: string;
	saving: boolean;
	onPayoutPayPalEmailChange: (value: string) => void;
	onCampaignLabelChange: (value: string) => void;
	onCampaignSourceChange: (value: string) => void;
	onCustomCodeChange: (value: string) => void;
	onSupportSubjectChange: (value: string) => void;
	onSupportMessageChange: (value: string) => void;
	onSavePayoutEmail: () => void;
	onStartPayoutSetup?: () => void;
	onCreateCampaignLink: () => void;
	onRequestCustomCode: () => void;
	onSendSupportRequest: (details: AffiliateSupportDetails) => void;
	supportAttachmentsEnabled?: boolean;
}) => {
	const data = useMemo(
		() => (rawData ? normalizeDashboardData(rawData) : null),
		[rawData],
	);
	const location = useLocation();
	const dashboardSearch = location.search || "";
	const buildDashboardPath = (targetSection: AffiliateDashboardSection) =>
		`${targetSection === "overview" ? basePath : `${basePath}/${targetSection}`}${dashboardSearch}`;
	const activeSection = normalizeAffiliateSection(section);
	const stats = data?.stats;
	const currency = data?.settings?.currency || "AUD";
	const latestWeek = latestPoint(data?.performance?.weekly || []);
	const latestMonth = latestPoint(data?.performance?.monthly || []);
	const headerStats = [
		{ label: "Status", value: data?.status || "Loading" },
		{ label: "Promo code", value: data?.promoCode || data?.code || "Pending" },
		{ label: "Conversion", value: `${stats ? conversionRate(stats) : 0}%` },
		{ label: "Approved", value: money(stats?.payableEarnings || 0, currency) },
	];
	const [supportPriority, setSupportPriority] = useState(supportPriorityOptions[0]);
	const [supportCampaign, setSupportCampaign] = useState("");
	const [supportContactPreference, setSupportContactPreference] = useState(
		contactPreferenceOptions[0],
	);
	const [supportAttachmentFile, setSupportAttachmentFile] = useState<File | null>(
		null,
	);

	const sendSupport = () => {
		onSendSupportRequest({
			priority: supportPriority,
			campaign: supportCampaign,
			contactPreference: supportContactPreference,
			attachmentFile: supportAttachmentsEnabled ? supportAttachmentFile : null,
			attachmentName:
				supportAttachmentsEnabled && supportAttachmentFile
					? supportAttachmentFile.name
					: "",
		});
		if (supportAttachmentsEnabled) {
			setSupportAttachmentFile(null);
		}
	};

	const trafficRows = useMemo(
		() =>
			(data?.trafficSources || []).map((source) => [
				cleanLabel(source.source),
				numberText(source.clicks),
				numberText(source.signups),
				numberText(source.paidUsers),
				`${source.conversionRate}%`,
				money(source.earnings, currency),
			]),
		[data?.trafficSources, currency],
	);

	const campaignRows = useMemo(
		() =>
			(data?.campaignLinks || []).map((link) => [
				<div>
					<div className="font-semibold text-slate-950">{link.label}</div>
					<div className="mt-1 text-xs text-slate-500">
						{cleanLabel(link.source)} · {dateText(link.createdAt)}
					</div>
				</div>,
				numberText(link.clicks),
				numberText(link.signups),
				numberText(link.paidUsers),
				money(link.earnings, currency),
				<div className="flex max-w-[320px] items-center gap-2">
					<span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-500">
						{link.link}
					</span>
					<CopyButton text={link.link} />
				</div>,
			]),
		[data?.campaignLinks, currency],
	);

	const page = () => {
		if (loading) {
			return (
				<div className={`${panelClass} p-6 text-sm text-slate-600`}>
					Loading affiliate dashboard...
				</div>
			);
		}

		if (!data) {
			return (
				<div className={`${panelClass} p-6`}>
					<div className="text-lg font-semibold text-slate-950">
						Dashboard unavailable
					</div>
					<p className="mt-2 text-sm leading-6 text-slate-600">
						{error || "Affiliate dashboard details could not be loaded."}
					</p>
				</div>
			);
		}

		if (activeSection === "journey") {
			const steps = [
				{
					label: "Clicked link",
					value: data.referralJourney.clickedLink,
					text: "A visitor opened an approved affiliate link.",
				},
				{
					label: "Signed up",
					value: data.referralJourney.signedUp,
					text: "The visitor created an Unjabbed account.",
				},
				{
					label: "Subscribed",
					value: data.referralJourney.subscribed,
					text: "The referred account became a paid member.",
				},
				{
					label: "Eligible commission",
					value: data.referralJourney.eligibleCommission,
					text: "The payment passed the waiting and safety checks.",
				},
			];
			return (
				<section className={panelClass}>
					<PageHeader
						title="Referral Journey"
						description="Track each step from link click to approved commission without crowding the dashboard into one long page."
					/>
					<div className="grid gap-4 p-5 2xl:grid-cols-4">
						{steps.map((step, index) => (
							<div
								key={step.label}
								className="rounded-md border border-slate-200 p-5"
							>
								<div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-950 text-sm font-semibold text-white">
									{index + 1}
								</div>
								<div className="mt-5 text-3xl font-semibold text-slate-950">
									{numberText(step.value)}
								</div>
								<div className="mt-2 font-semibold text-slate-950">
									{step.label}
								</div>
								<p className="mt-2 text-sm leading-6 text-slate-600">
									{step.text}
								</p>
							</div>
						))}
					</div>
				</section>
			);
		}

		if (activeSection === "performance") {
			return (
				<section className={panelClass}>
					<PageHeader
						title="Performance"
						description="Clicks, signups, paid members, conversion rate, and earnings by day, week, and month."
					/>
					<div className="grid gap-5 p-5 xl:grid-cols-[1.2fr_0.8fr]">
						<div>
							<SparkBars rows={data.performance.daily || []} />
							<div className="mt-3 text-sm text-slate-500">
								Daily clicks, newest activity on the right.
							</div>
						</div>
						<div className="grid gap-4">
							<MetricTile
								label="Latest week clicks"
								value={numberText(latestWeek.clicks)}
							/>
							<MetricTile
								label="Latest week earnings"
								value={money(latestWeek.earnings, currency)}
								tone="green"
							/>
							<MetricTile
								label="Latest month clicks"
								value={numberText(latestMonth.clicks)}
							/>
							<MetricTile
								label="Latest month earnings"
								value={money(latestMonth.earnings, currency)}
								tone="pink"
							/>
						</div>
					</div>
					<div className="grid gap-4 border-t border-slate-100 p-5 xl:grid-cols-5">
						{[
							["Clicks", (row: PerformancePoint) => row.clicks, numberText],
							["Signups", (row: PerformancePoint) => row.signups, numberText],
							[
								"Paid members",
								(row: PerformancePoint) => row.paidUsers,
								numberText,
							],
							[
								"Conversion rate",
								(row: PerformancePoint) => row.conversionRate,
								(value: number) => `${value}%`,
							],
							[
								"Earnings",
								(row: PerformancePoint) => row.earnings,
								(value: number) => money(value, currency),
							],
						].map(([label, valueFor, formatter]) => (
							<div
								key={String(label)}
								className="rounded-md border border-slate-200 p-4"
							>
								<div className="mb-3 text-sm font-semibold text-slate-950">
									{String(label)}
								</div>
								<div className="grid gap-3">
									<MetricBars
										rows={data.performance.daily || []}
										valueFor={valueFor as (row: PerformancePoint) => number}
										formatter={formatter as (value: number) => string}
										periodLabel="Daily"
									/>
									<MetricBars
										rows={data.performance.weekly || []}
										valueFor={valueFor as (row: PerformancePoint) => number}
										formatter={formatter as (value: number) => string}
										periodLabel="Weekly"
									/>
									<MetricBars
										rows={data.performance.monthly || []}
										valueFor={valueFor as (row: PerformancePoint) => number}
										formatter={formatter as (value: number) => string}
										periodLabel="Monthly"
									/>
								</div>
							</div>
						))}
					</div>
					<div className="grid gap-4 border-t border-slate-100 p-5 sm:grid-cols-2 xl:grid-cols-5">
						<MetricTile label="Clicks" value={numberText(data.stats.clicks)} />
						<MetricTile
							label="Signups"
							value={numberText(data.stats.signups)}
						/>
						<MetricTile
							label="Paid members"
							value={numberText(data.stats.paidUsers)}
						/>
						<MetricTile
							label="Tracked sales"
							value={money(data.stats.grossTrackedAmount, currency)}
						/>
						<MetricTile
							label="Conversion rate"
							value={`${conversionRate(data.stats)}%`}
						/>
					</div>
				</section>
			);
		}

		if (activeSection === "links") {
			return (
				<div className="grid gap-5">
					<section className={panelClass}>
						<PageHeader
							title="Campaign Links"
							description="Create a labelled link for each video, post, email, website, or community mention."
							action={<CopyButton text={data.link} label="Copy main link" />}
						/>
						<div className="grid gap-4 p-5 xl:grid-cols-[1fr_0.8fr]">
							<div className="rounded-md border border-pink-200 bg-pink-50 p-4">
								<div className="text-sm font-semibold text-pink-900">
									Main affiliate link
								</div>
								<div className="mt-2 break-all font-mono text-sm text-pink-800">
									{data.link}
								</div>
							</div>
							<div className="rounded-md border border-slate-200 p-4">
								<div className="text-sm font-semibold text-slate-950">
									Promo code
								</div>
								<div className="mt-2 flex items-center justify-between gap-3">
									<div className="break-words text-2xl font-semibold text-slate-950">
										{data.promoCode || data.code || "Pending"}
									</div>
									<CopyButton text={data.promoCode || data.code} />
								</div>
							</div>
						</div>
						<div className="grid gap-4 border-t border-slate-100 p-5 xl:grid-cols-[0.8fr_1.2fr]">
							<div className="rounded-md border border-slate-200 p-4">
								<div className="text-sm font-semibold text-slate-950">
									Personal code
								</div>
								<div className="mt-2 break-words text-2xl font-semibold text-slate-950">
									{data.customCodeRequest?.code ||
										data.promoCode ||
										data.code ||
										"Pending"}
								</div>
								<div className="mt-2 text-sm text-slate-500">
									{customCodeStatusText(data.customCodeRequest)}
								</div>
								{data.assignedCode && data.assignedCode !== data.promoCode && (
									<div className="mt-2 text-xs text-slate-500">
										Assigned backup code: {data.assignedCode}
									</div>
								)}
							</div>
							<div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
								<input
									className={inputClass}
									value={customCode}
									onChange={(event) => onCustomCodeChange(event.target.value)}
									placeholder="Choose a personal code"
								/>
								<CustomButton loading={saving} onClick={onRequestCustomCode}>
									Request Code
								</CustomButton>
								<div className="text-sm leading-6 text-slate-500 sm:col-span-2">
									Clean names are approved automatically. Risky or protected
									names are held for review.
								</div>
							</div>
						</div>
						<div className="grid gap-3 border-t border-slate-100 p-5 lg:grid-cols-[1fr_180px_auto]">
							<input
								className={inputClass}
								value={campaignLabel}
								onChange={(event) => onCampaignLabelChange(event.target.value)}
								placeholder="Campaign label"
							/>
							<select
								className={inputClass}
								value={campaignSource}
								onChange={(event) => onCampaignSourceChange(event.target.value)}
							>
								{[
									"YouTube",
									"Facebook",
									"Email",
									"Website",
									"Podcast",
									"Telegram",
									"X",
									"Other",
								].map((source) => (
									<option key={source} value={source}>
										{source}
									</option>
								))}
							</select>
							<CustomButton loading={saving} onClick={onCreateCampaignLink}>
								Create Link
							</CustomButton>
						</div>
					</section>

					<section className={panelClass}>
						<PageHeader
							title="Custom Links"
							description="See which campaign link is getting clicks, signups, paid members, and earnings."
						/>
						<div className="p-5">
							<DataTable
								columns={[
									"Campaign",
									"Clicks",
									"Signups",
									"Paid",
									"Earnings",
									"Link",
								]}
								rows={campaignRows}
								emptyText="No custom campaign links yet."
							/>
						</div>
					</section>

					<section className={panelClass}>
						<PageHeader
							title="Traffic Sources"
							description="Compare YouTube, Facebook, email, website, and other channels."
						/>
						<div className="p-5">
							<DataTable
								columns={[
									"Source",
									"Clicks",
									"Signups",
									"Paid",
									"Rate",
									"Earnings",
								]}
								rows={trafficRows}
								emptyText="No traffic sources tracked yet."
							/>
						</div>
					</section>
				</div>
			);
		}

		if (activeSection === "kit") {
			const groups = [
				["Captions", data.marketingKit.captions],
				["Email copy", data.marketingKit.emailCopy],
				["Disclosure copy", data.marketingKit.disclosureCopy],
				["Short video scripts", data.marketingKit.videoScripts],
				["Posting rules", data.marketingKit.postingRules],
			] as Array<[string, string[]]>;
			return (
				<div className="grid gap-5">
					<section className={panelClass}>
						<PageHeader
							title="Approved Marketing Kit"
							description="Use approved banners, video ads, captions, email copy, disclosures, scripts, and posting rules. Private Canva source projects are not shown."
						/>
						<div className="grid gap-4 p-5 lg:grid-cols-2">
							{data.marketingKit.banners.length === 0 ? (
								<div className="rounded-md border border-slate-200 p-5 text-sm text-slate-500">
									No banners are available yet.
								</div>
							) : (
								data.marketingKit.banners.map((creative) => (
									<div
										key={creative.id}
										className="rounded-md border border-slate-200 p-4"
									>
										{creative.previewUrl && (
											<div className="flex h-[260px] items-center justify-center rounded-md border border-slate-200 bg-slate-950 p-3 sm:h-[320px]">
												<img
													src={creative.previewUrl}
													alt={creative.title}
													className="max-h-full max-w-full object-contain"
												/>
											</div>
										)}
										<div className="mt-4 text-base font-semibold text-slate-950">
											{creative.title}
										</div>
										<div className="mt-1 text-sm text-slate-500">
											{creative.dimensions} · {creative.format}
										</div>
										<p className="mt-2 text-sm leading-6 text-slate-600">
											{creative.notes}
										</p>
										<div className="mt-4">
											<CustomButton
												small
												onClick={() =>
													window.open(
														creative.downloadUrl,
														"_blank",
														"noopener,noreferrer",
													)
												}
											>
												Open Asset
											</CustomButton>
										</div>
									</div>
								))
							)}
						</div>
					</section>

					<section className={panelClass}>
						<PageHeader
							title="Video Ads"
							description="Share the YouTube ad link together with your tracked affiliate link. If a platform only allows one clickable link, use the tracked affiliate link."
						/>
						<div className="grid gap-4 p-5 lg:grid-cols-3">
							{data.marketingKit.videoAds.length === 0 ? (
								<div className="rounded-md border border-slate-200 p-5 text-sm text-slate-500">
									No video ads are available yet.
								</div>
							) : (
								data.marketingKit.videoAds.map((video) => {
									const videoAffiliateLink = buildVideoAffiliateLink(
										data,
										video,
									);
									const sharePost = buildVideoSharePost(data, video);
									return (
										<div
											key={video.id}
											className="flex min-w-0 flex-col rounded-md border border-slate-200 p-4"
										>
											<a
												href={video.videoUrl}
												target="_blank"
												rel="noreferrer"
												className="relative block overflow-hidden rounded-md border border-slate-200 bg-slate-950"
											>
												<img
													src={video.thumbnailUrl}
													alt={video.title}
													className="aspect-video w-full object-cover"
												/>
												<span className="absolute bottom-2 right-2 rounded bg-slate-950/90 px-2 py-1 text-xs font-semibold text-white">
													{video.duration}
												</span>
											</a>
											<div className="mt-4 text-base font-semibold leading-6 text-slate-950">
												{video.title}
											</div>
											<p className="mt-2 flex-1 text-sm leading-6 text-slate-600">
												{video.suggestedCaption}
											</p>
											<div className="mt-4 grid gap-2">
												<CopyButton
													text={sharePost}
													label="Copy Post"
													wide
												/>
												<div className="grid grid-cols-2 gap-2">
													<CopyButton
														text={video.videoUrl}
														label="Video Link"
														wide
													/>
													<CopyButton
														text={videoAffiliateLink}
														label="Affiliate Link"
														wide
													/>
												</div>
											</div>
										</div>
									);
								})
							)}
						</div>
					</section>

					<section className={panelClass}>
						<PageHeader
							title="Copy And Rules"
							description="Approved text snippets for posts, emails, video scripts, and disclosures."
						/>
						<div className="grid gap-4 p-5 lg:grid-cols-2">
							{groups.map(([title, rows]) => (
								<div
									key={title}
									className="rounded-md border border-slate-200 p-4"
								>
									<div className="font-semibold text-slate-950">{title}</div>
									<div className="mt-3 grid gap-3">
										{rows.length === 0 ? (
											<div className="text-sm text-slate-500">
												Nothing added yet.
											</div>
										) : (
											rows.slice(0, 4).map((row) => (
												<p
													key={row}
													className="text-sm leading-6 text-slate-600"
												>
													{row}
												</p>
											))
										)}
									</div>
								</div>
							))}
						</div>
					</section>
				</div>
			);
		}

		if (activeSection === "payouts") {
			const payoutRows = data.payoutHistory.map((payout) => [
				<div className="font-semibold text-slate-950">
					{money(payout.amount, payout.currency || currency)}
				</div>,
				cleanLabel(payout.status),
				cleanLabel(payout.provider),
				dateText(payout.paidAt || payout.createdAt),
				payout.failureReason || "None",
			]);
			return (
				<div className="grid gap-5">
					<section className={panelClass}>
						<PageHeader
							title="Payout Setup"
							description="Keep PayPal payout details ready. Unjabbed does not collect bank details in the app."
						/>
						<div className="grid gap-5 p-5 xl:grid-cols-[0.8fr_1.2fr]">
							<div className="rounded-md border border-slate-200 p-4">
								<div className="text-sm text-slate-500">
									Payment method status
								</div>
								<div className="mt-2 text-xl font-semibold text-slate-950">
									{cleanLabel(data.payoutSetupStatus || "not started")}
								</div>
								<p className="mt-2 text-sm leading-6 text-slate-600">
									{data.payoutSetupMessage}
								</p>
							</div>
							<div className="grid gap-3 sm:grid-cols-[1fr_auto]">
								<input
									className={inputClass}
									value={payoutPayPalEmail}
									onChange={(event) =>
										onPayoutPayPalEmailChange(event.target.value)
									}
									placeholder="PayPal payout email"
								/>
								<CustomButton loading={saving} onClick={onSavePayoutEmail}>
									Save
								</CustomButton>
							</div>
							{onStartPayoutSetup && (
								<div className="mt-4">
									<CustomButton onClick={onStartPayoutSetup}>
										Start Payout Setup
									</CustomButton>
								</div>
							)}
						</div>
					</section>

					<section className={panelClass}>
						<PageHeader
							title="Payout History"
							description="Past payouts, payout status, failed payout reason, and payment method status."
						/>
						<div className="p-5">
							<DataTable
								columns={[
									"Amount",
									"Status",
									"Method",
									"Date",
									"Failed reason",
								]}
								rows={payoutRows}
								emptyText="No payouts have been sent yet."
							/>
						</div>
					</section>
				</div>
			);
		}

		if (activeSection === "rules") {
			return (
				<div className="grid gap-5 xl:grid-cols-[1fr_0.85fr]">
					<section className={panelClass}>
						<PageHeader
							title="Commission Rules"
							description="What counts, what is blocked, refunds, waiting period, and payout checks."
						/>
						<div className="grid gap-3 p-5 sm:grid-cols-3">
							<ShortcutLink
								to={buildDashboardPath("kit")}
								icon="kit"
								title="Marketing Kit"
								description="Approved captions and assets"
							/>
							<ShortcutLink
								to={buildDashboardPath("payouts")}
								icon="payouts"
								title="Payouts"
								description="Setup, status, and history"
							/>
							<ShortcutLink
								to={buildDashboardPath("rules")}
								icon="more"
								title="Rules"
								description="Commission and safety policy"
							/>
							<ShortcutLink
								to={buildDashboardPath("notifications")}
								icon="more"
								title="Updates"
								description="Affiliate account alerts"
							/>
							<ShortcutLink
								to={buildDashboardPath("support")}
								icon="more"
								title="Support"
								description="Ask for help or a review"
							/>
						</div>
						<div className="grid gap-4 p-5">
							{data.commissionRules.length === 0 ? (
								<div className="rounded-md border border-slate-200 p-5 text-sm text-slate-500">
									No commission rules are available yet.
								</div>
							) : (
								data.commissionRules.map((rule) => (
									<div
										key={rule.title}
										className="rounded-md border border-slate-200 p-4"
									>
										<div className="font-semibold text-slate-950">
											{rule.title}
										</div>
										<p className="mt-2 text-sm leading-6 text-slate-600">
											{rule.description}
										</p>
									</div>
								))
							)}
						</div>
					</section>
					<section className={panelClass}>
						<PageHeader
							title="Tier Progress"
							description="Current tier, next tier, and bonus target progress."
						/>
						<div className="p-5">
							<div className="rounded-md border border-slate-200 p-5">
								<div className="text-sm text-slate-500">Current tier</div>
								<div className="mt-2 text-3xl font-semibold text-slate-950">
									{data.tierProgress.currentTier}
								</div>
								<div className="mt-6 flex items-center justify-between text-sm">
									<span className="text-slate-600">
										Next: {data.tierProgress.nextTier}
									</span>
									<span className="font-semibold text-pink-700">
										{data.tierProgress.percent}%
									</span>
								</div>
								<div className="mt-3">
									<ProgressBar percent={data.tierProgress.percent} />
								</div>
								<div className="mt-3 text-sm text-slate-500">
									{data.tierProgress.paidUsers} of{" "}
									{data.tierProgress.targetPaidUsers} paid members
								</div>
							</div>
						</div>
					</section>
				</div>
			);
		}

		if (activeSection === "notifications") {
			return (
				<section className={panelClass}>
					<PageHeader
						title="Notifications"
						description="Affiliate updates such as new signup, commission approved, payout sent, or payout info needed."
					/>
					<div className="grid gap-3 p-5">
						{data.notifications.length === 0 ? (
							<div className="rounded-md border border-slate-200 p-5 text-sm text-slate-500">
								No affiliate notifications yet.
							</div>
						) : (
							data.notifications.map((notice) => (
								<div
									key={notice.id}
									className="grid gap-3 rounded-md border border-slate-200 p-4 md:grid-cols-[1fr_auto]"
								>
									<div>
										<div className="font-semibold text-slate-950">
											{notice.title}
										</div>
										<p className="mt-1 text-sm leading-6 text-slate-600">
											{notice.message}
										</p>
									</div>
									<div className="text-sm text-slate-500">
										{dateText(notice.createdAt)}
									</div>
								</div>
							))
						)}
					</div>
				</section>
			);
		}

		if (activeSection === "support") {
			return (
					<section className={panelClass}>
						<PageHeader
							title="Support"
							description="Send affiliate support the details needed to check payments, tracking, links, campaign assets, or account access."
						/>
						<div className="grid gap-5 pt-5">
							<div className="grid gap-4 lg:grid-cols-[1fr_220px]">
								<label className="grid gap-2 text-sm font-semibold text-slate-950">
									Subject
									<select
										className={inputClass}
										value={supportSubject}
										onChange={(event) =>
											onSupportSubjectChange(event.target.value)
										}
									>
										<option value="" disabled>
											Choose a support type
										</option>
										{supportSubjectOptions.map((option) => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>
								</label>
								<label className="grid gap-2 text-sm font-semibold text-slate-950">
									Priority
									<select
										className={inputClass}
										value={supportPriority}
										onChange={(event) => setSupportPriority(event.target.value)}
									>
										{supportPriorityOptions.map((option) => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>
								</label>
							</div>
							<div className="grid gap-4 lg:grid-cols-[1fr_220px]">
								<label className="grid gap-2 text-sm font-semibold text-slate-950">
									Campaign, code, or link
									<input
										className={inputClass}
										value={supportCampaign}
										onChange={(event) => setSupportCampaign(event.target.value)}
										placeholder="Paste the campaign name, code, or link"
									/>
								</label>
								<label className="grid gap-2 text-sm font-semibold text-slate-950">
									Best reply method
									<select
										className={inputClass}
										value={supportContactPreference}
										onChange={(event) =>
											setSupportContactPreference(event.target.value)
										}
									>
										{contactPreferenceOptions.map((option) => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>
								</label>
							</div>
							<label className="grid gap-2 text-sm font-semibold text-slate-950">
								Details
								<textarea
									className={textareaClass}
									value={supportMessage}
									onChange={(event) =>
										onSupportMessageChange(event.target.value)
									}
									placeholder="Add what happened, when it happened, and what result you expected."
								/>
							</label>
							{supportAttachmentsEnabled ? (
								<div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4">
									<label className="grid cursor-pointer gap-2 text-sm font-semibold text-slate-950">
										Image or file
										<input
											type="file"
											accept="image/*,.pdf"
											className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-md file:border-0 file:bg-slate-950 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white"
											onChange={(event) =>
												setSupportAttachmentFile(
													event.target.files?.[0] || null,
												)
											}
										/>
									</label>
									<div className="mt-2 text-xs leading-5 text-slate-500">
										Add a screenshot, payout proof, or campaign image. Please avoid
										uploading passwords, private documents, or unrelated source files.
									</div>
									{supportAttachmentFile && (
										<div className="mt-3 flex flex-col gap-2 rounded-md bg-white p-3 text-sm text-slate-700 sm:flex-row sm:items-center sm:justify-between">
											<span className="min-w-0 break-all">
												{supportAttachmentFile.name}
											</span>
											<button
												type="button"
												className="text-left text-sm font-semibold text-pink-700"
												onClick={() => setSupportAttachmentFile(null)}
											>
												Remove
											</button>
										</div>
									)}
								</div>
							) : (
								<div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
									For screenshot evidence, describe the file in the message and
									support will request it by email if needed.
								</div>
							)}
							<div className="grid gap-3 rounded-md border border-slate-200 p-4 text-sm text-slate-600 md:grid-cols-3">
								<div>
									<div className="font-semibold text-slate-950">Good to include</div>
									<div className="mt-1">date, campaign name, link, amount, and email used</div>
								</div>
								<div>
									<div className="font-semibold text-slate-950">Sensitive info</div>
									<div className="mt-1">do not send passwords or full payment details</div>
								</div>
								<div>
									<div className="font-semibold text-slate-950">Follow-up</div>
									<div className="mt-1">support can review tracking and payout history</div>
								</div>
							</div>
							<div className="flex justify-start">
								<CustomButton loading={saving} onClick={sendSupport}>
									Send Request
								</CustomButton>
							</div>
						</div>
					</section>
				);
			}

				return (
					<div className="grid gap-5">
						<section className={panelClass}>
							<PageHeader
								title="Earnings Overview"
								description="Pending, approved, blocked, paid, next payout date, and minimum payout progress."
								action={<CopyButton text={data.link} label="Copy affiliate link" />}
							/>
							<div className="grid gap-4 p-5 sm:grid-cols-2 2xl:grid-cols-4">
								<MetricTile
									label="Pending earnings"
									value={money(data.stats.pendingEarnings, currency)}
									tone="amber"
									detail="Awaiting checks"
								/>
								<MetricTile
									label="Approved earnings"
									value={money(data.stats.payableEarnings, currency)}
									tone="green"
									detail="Ready when minimum is met"
								/>
								<MetricTile
									label="Blocked earnings"
									value={money(data.stats.blockedEarnings, currency)}
									tone="red"
									detail="Held or rejected"
								/>
								<MetricTile
									label="Paid earnings"
									value={money(data.stats.paidEarnings, currency)}
									tone="pink"
									detail="Already sent"
								/>
							</div>
							<div className="grid gap-4 border-t border-slate-100 p-5 2xl:grid-cols-[1.35fr_0.65fr]">
								<GrowthLineChart
									rows={data.performance.daily || []}
									currency={currency}
								/>
								<div className="grid gap-4">
									<SignupActivity data={data} />
									<RecentReferralActivity data={data} currency={currency} />
								</div>
							</div>
							<div className="grid gap-4 border-t border-slate-100 p-5 2xl:grid-cols-[1fr_1fr]">
								<div className="rounded-lg border border-slate-200/80 bg-white p-5 shadow-[0_12px_30px_rgba(15,23,42,0.04)]">
									<div className="text-sm font-semibold text-slate-500">Next payout date</div>
									<div className="mt-2 text-2xl font-semibold text-slate-950">
										{dateText(data.nextPayoutDate)}
									</div>
								</div>
								<div className="rounded-lg border border-slate-200/80 bg-white p-5 shadow-[0_12px_30px_rgba(15,23,42,0.04)]">
								<div className="flex items-center justify-between gap-3">
									<div className="text-sm font-semibold text-slate-500">Minimum payout</div>
									<div className="text-lg font-semibold text-slate-950">
										{data.minimumPayoutProgress.percent}%
									</div>
								</div>
								<div className="mt-3">
									<ProgressBar percent={data.minimumPayoutProgress.percent} />
								</div>
								<div className="mt-3 text-sm text-slate-500">
									{money(data.minimumPayoutProgress.payableAmount, currency)} of{" "}
									{money(data.minimumPayoutProgress.minimumAmount, currency)}{" "}
									ready
								</div>
							</div>
						</div>
					</section>

					<section className={panelClass}>
						<PageHeader
							title="Affiliate Link And Disclosure"
								description="Share only the approved link, promo code, and disclosure copy."
							/>
							<div className="grid gap-4 p-5 xl:grid-cols-[1.2fr_0.8fr]">
							<div className="rounded-lg border border-pink-200 bg-pink-50 p-4">
								<div className="text-sm font-semibold text-pink-900">
									Affiliate link
								</div>
								<div className="mt-2 break-all font-mono text-sm text-pink-800">
									{data.link}
								</div>
							</div>
							<div className="rounded-lg border border-slate-200 p-4">
								<div className="text-sm font-semibold text-slate-950">
									Disclosure copy
								</div>
								<p className="mt-2 text-sm leading-6 text-slate-600">
									{data.disclosureText ||
										"I may earn a commission if you sign up or subscribe through my Unjabbed link."}
								</p>
							</div>
						</div>
					</section>

					<section className={panelClass}>
						<PageHeader
							title="Dashboard Shortcuts"
								description="Open the full dashboard areas from one phone-friendly home screen."
							/>
							<div className="grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-3">
							<ShortcutLink
								to={buildDashboardPath("performance")}
								icon="stats"
								title="Full Stats"
								description="Daily, weekly, and monthly results"
							/>
							<ShortcutLink
								to={buildDashboardPath("links")}
								icon="links"
								title="Campaign Links"
								description="Create and track promo links"
							/>
							<ShortcutLink
								to={buildDashboardPath("kit")}
								icon="kit"
								title="Marketing Kit"
								description="Approved captions and assets"
							/>
							<ShortcutLink
								to={buildDashboardPath("payouts")}
								icon="payouts"
								title="Payouts"
								description="Setup, status, and history"
							/>
							<ShortcutLink
								to={buildDashboardPath("notifications")}
								icon="more"
								title="Updates"
								description="Affiliate account notifications"
							/>
							<ShortcutLink
								to={buildDashboardPath("support")}
								icon="more"
								title="Support"
								description="Tracking, payout, or account help"
							/>
						</div>
					</section>
				</div>
			);
		};

		return (
			<WorkspaceShell
				title={data?.profile?.displayName || "Affiliate Dashboard"}
				subtitle="Manage earnings, links, payouts, creative, and support."
				contextLabel={contextLabel}
				basePath={basePath}
				activeSection={activeSection}
				headerStats={headerStats}
			>
				<div className="grid gap-4">
					{error && <StatusMessage tone="error">{error}</StatusMessage>}
					{message && <StatusMessage tone="success">{message}</StatusMessage>}
					{page()}
				</div>
		</WorkspaceShell>
	);
};
